import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import confetti from 'canvas-confetti';
import { selectedClientSelector } from '../../store/slices/selectedClient';
import { initiateGoogleOAuth } from '../../services/googleOAuth';
import { getGoogleProfileInfo } from '../../components/GoogleBusinessProfile/services';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  ExternalLink,
  Pencil,
  Upload,
  X,
  LogIn,
  Info,
  Loader2
} from 'lucide-react';
import {
  BusinessHoursSelect,
  DAYS as WEEK_DAYS,
  defaultWeekHours,
  cloneWeekHours
} from '../../components/BusinessHoursSelect/index.tsx';
import {
  canContinueAfterGoogleConnection,
  mapGoogleLocationToAnswers
} from './googleLocation';
import logo from '../../assets/logo.png';
import layoutHvac from '../../assets/layout-hvac.jpg';
import layoutPlumber from '../../assets/layout-plumber.jpg';
import layoutElectrician from '../../assets/layout-electrician.jpg';

const S_DETAILS = 'Business Details';
const S_HOURS = 'Hours & Services';
const S_POSTS = 'Your Social Media Posts';
const S_TEAM = 'Your Team';
const S_ACCESS = 'Account Access';
const S_GOALS = 'Goals and Expectations';
const S_TOUCH = 'Staying in Touch';

const HOME_TYPE = 'Home services (plumbing, HVAC, roofing, etc.)';
const TYPE_OPTIONS = [
  HOME_TYPE,
  'Other local business (restaurant, dentist, auto shop, etc.)'
];

const HOME_SERVICES = [
  'Air Duct Cleaning',
  'Appliance Repair',
  'Artificial Turf Installation',
  'Bathroom Remodeling',
  'Cabinet Installation & Refinishing',
  'Carpet Cleaning',
  'Chimney Cleaning & Repair',
  'Concrete Contractor',
  'Deck & Patio Contractor',
  'Door Installation & Repair',
  'Drain Cleaning',
  'Dryer Vent Cleaning',
  'Electrician',
  'Fencing Contractor',
  'Flooring Contractor',
  'Foundation Repair',
  'Garage Door Repair & Installation',
  'General Contractor',
  'Generator Installation',
  'Gutter Installation & Cleaning',
  'Handyman',
  'Home Cleaning',
  'Home Inspection',
  'Home Remodeling',
  'Home Security Installation',
  'HVAC (Heating & Air Conditioning)',
  'Insulation Contractor',
  'Irrigation & Sprinkler Services',
  'Junk Removal',
  'Kitchen Remodeling',
  'Landscaping',
  'Lawn Care',
  'Locksmith',
  'Masonry Contractor',
  'Mold Remediation',
  'Moving Company',
  'Painting Contractor',
  'Paver Installation',
  'Pest Control',
  'Plumber',
  'Pool Cleaning & Maintenance',
  'Pool Construction & Remodeling',
  'Pressure Washing',
  'Roofing Contractor',
  'Septic Tank Services',
  'Siding Contractor',
  'Solar Panel Installation',
  'Tree Services',
  'Water Damage Restoration',
  'Water Filtration & Treatment',
  'Waterproofing (Basement & Foundation)',
  'Window Cleaning',
  'Window Installation & Replacement',
  'Other (please describe)'
];

const OTHER_INDUSTRIES = [
  'Restaurant',
  'Cafe / Coffee Shop',
  'Bakery',
  'Dentist',
  'Doctor / Clinic',
  'Chiropractor',
  'Hair Salon',
  'Barber',
  'Spa / Massage',
  'Fitness / Gym',
  'Auto Repair',
  'Car Wash',
  'Other (please describe)'
];

const TIMEZONES = [
  'Eastern',
  'Central',
  'Mountain',
  'Mountain – Arizona (no daylight saving)',
  'Pacific',
  'Alaska',
  'Hawaii'
];

const US_HOLIDAYS = [
  "New Year's Day",
  'Martin Luther King Jr. Day',
  "Presidents' Day",
  'Memorial Day',
  'Juneteenth',
  'Independence Day',
  'Labor Day',
  'Columbus Day',
  'Veterans Day',
  'Thanksgiving Day',
  'Christmas Day'
];
const CA_HOLIDAYS = [
  'Victoria Day',
  'Canada Day',
  'Canadian Thanksgiving',
  'Remembrance Day'
];
const ALL_HOLIDAYS = [...US_HOLIDAYS, ...CA_HOLIDAYS];

const GOALS = [
  'Get more phone calls from potential customers',
  'Generate more leads and service requests',
  'Book more jobs and appointments',
  'Rank higher on Google Search and Maps',
  'Get found and recommended in AI searches',
  'Get more customer reviews',
  'Build a stronger online reputation',
  'Increase brand awareness in my local area',
  'Expand into new service areas',
  'Grow my business and increase revenue'
];

const noWebsite = (v) => !!v.website?.none;

const QUESTIONS = [
  {
    id: 'name',
    section: S_DETAILS,
    type: 'text',
    title:
      "What's your business name? Enter it exactly as you want it to appear on Google.",
    description:
      "Use your real business name only. Don't add keywords or city names — Google can suspend listings that do.",
    placeholder: 'e.g. Smith Family Plumbing'
  },
  {
    id: 'logo',
    section: S_DETAILS,
    type: 'file',
    title: 'Upload your business logo.',
    description: 'No logo? Skip this — we can help.'
  },
  {
    id: 'phone',
    section: S_DETAILS,
    type: 'phone',
    title: "What's your main business phone number?",
    description: 'This is the number customers will call.',
    placeholder: '(555) 123-4567'
  },
  {
    id: 'phone2',
    section: S_DETAILS,
    type: 'phone',
    title: 'Second phone number (optional)',
    description: 'For example, a cell or after-hours line.',
    placeholder: '(555) 987-6543'
  },
  {
    id: 'phoneShown',
    section: S_DETAILS,
    type: 'radio',
    title: 'Which number should we show on Google and your other listings?',
    options: ['Main number', 'Second number'],
    show: (v) => !!v.phone2?.trim()
  },
  {
    id: 'businessType',
    section: S_DETAILS,
    type: 'businessType',
    title: 'Which category best describes your business?'
  },
  {
    id: 'address',
    section: S_DETAILS,
    type: 'address',
    title: "What's your business address?",
    description:
      'Use the address on your Google listing. Work from home? Tell us and we can hide your address on Google.'
  },
  {
    id: 'timezone',
    section: S_DETAILS,
    type: 'radio',
    title: 'What time zone is your business in?',
    options: TIMEZONES
  },
  {
    id: 'areas',
    section: S_DETAILS,
    type: 'textarea',
    title: 'Which cities or towns do you serve?',
    description: 'One per line, most important first. Google shows up to 20.',
    placeholder: 'Miami\nCoral Gables\nDoral'
  },
  {
    id: 'website',
    section: S_DETAILS,
    type: 'website',
    title: "What's your website address?",
    placeholder: 'www.yourbusiness.com'
  },
  {
    id: 'email',
    section: S_DETAILS,
    type: 'email',
    title: 'What email address should customers use to contact you?',
    placeholder: 'you@yourbusiness.com'
  },
  {
    id: 'special',
    section: S_DETAILS,
    type: 'textarea',
    title: 'What makes your business stand out? Tell us in a few sentences.',
    description:
      'For example: years in business, family-owned, licensed and insured, warranties, same-day service, awards.'
  },
  {
    id: 'licensed',
    section: S_DETAILS,
    type: 'radio',
    title: 'Are you licensed and insured?',
    options: [
      'Licensed and insured',
      'Licensed only',
      'Insured only',
      'Neither'
    ],
    followUp: {
      when: (o) => o.toLowerCase().includes('licensed'),
      label: "What's your license number?"
    }
  },
  {
    id: 'yearStarted',
    section: S_DETAILS,
    type: 'text',
    title: 'What year did you start your business?',
    placeholder: 'e.g. 2012'
  },

  {
    id: 'hours',
    section: S_HOURS,
    type: 'hours',
    title: 'What are your business hours?',
    description: "Turn off any day you're not open, or check Open 24 hours."
  },
  {
    id: 'services',
    section: S_HOURS,
    type: 'textarea',
    title: 'List every service you offer.',
    description:
      'One per line. Be specific — "water heater repair" and "water heater installation" are separate services. A complete list helps you show up for more searches.',
    placeholder:
      'Drain cleaning\nWater heater repair\nWater heater installation'
  },
  {
    id: 'topServices',
    section: S_HOURS,
    type: 'textarea',
    title: 'Which 3 services do you want more calls for?',
    description:
      "Usually your most profitable jobs. We'll focus on these first.",
    placeholder:
      'Water heater installation\nBathroom remodels\nEmergency repairs'
  },

  {
    id: 'specials',
    section: S_POSTS,
    type: 'textarea',
    title: 'Are you running any specials right now?',
    description:
      'For example: $50 off AC tune-ups, free estimates, senior discount. Leave blank if none.'
  },
  {
    id: 'estimates',
    section: S_POSTS,
    type: 'checkboxes',
    title: 'Do you offer free estimates or financing?',
    description: 'Check all that apply.',
    options: ['Free estimates', 'Financing', 'Neither'],
    exclusive: 'Neither'
  },
  {
    id: 'avoid',
    section: S_POSTS,
    type: 'textarea',
    title: 'Is there anything we should never mention in your posts?',
    description:
      'For example: services you no longer offer, prices, competitor names. Leave blank if none.'
  },
  {
    id: 'holidays',
    section: S_POSTS,
    type: 'holidays',
    title: 'Which holidays would you like us to post about?',
    description: 'Check all that apply.'
  },
  {
    id: 'platforms',
    section: S_POSTS,
    type: 'checkboxes',
    title: 'Where should we post for you?',
    description: 'Check all that apply.',
    options: ['Facebook', 'Instagram', 'Google']
  },
  {
    id: 'images',
    section: S_POSTS,
    type: 'radio',
    title: 'What kind of images should we use in your posts?',
    options: [
      'Real photos when I have them, AI images to fill the gaps',
      'Real photos only'
    ],
    note: {
      when: (o) => o === 'Real photos only',
      text: 'Please upload photos in the next steps so we have enough to post.'
    }
  },
  {
    id: 'photos',
    section: S_POSTS,
    type: 'files',
    title: 'Upload photos of your work, team, trucks or shop.',
    description:
      'Before-and-after job photos work great. Real photos help your Google listing get more calls. JPG or PNG.'
  },
  {
    id: 'hashtags',
    section: S_POSTS,
    type: 'text',
    title: 'Any hashtags you want us to use?',
    description:
      "For example: #SmithPlumbing. Leave blank and we'll pick them for you.",
    placeholder: '#SmithPlumbing #MiamiPlumber'
  },

  {
    id: 'teamSize',
    section: S_TEAM,
    type: 'radio',
    title: 'How many people work in your business, including you?',
    options: ['Just me', '2–5', '6–20', 'More than 20']
  },

  {
    id: 'gbpManager',
    section: S_ACCESS,
    type: 'fields',
    title: 'Who manages your Google Business Profile right now?',
    fields: [
      { label: 'Their name' },
      { label: 'Email they use to log in to Google' }
    ],
    unsureLabel: "I'm not sure",
    show: (_v, c) => !c.google
  },
  {
    id: 'domain',
    section: S_ACCESS,
    type: 'fields',
    title: 'Who is your domain registered with?',
    fields: [
      {
        label: 'Domain company',
        placeholder: 'e.g. GoDaddy, Namecheap, Google Domains'
      },
      { label: 'Domain account username' },
      { label: 'Domain account password', password: true }
    ],
    show: (v) => !noWebsite(v)
  },
  {
    id: 'socialLinks',
    section: S_ACCESS,
    type: 'fields',
    title: 'Links to your Facebook and Instagram pages',
    fields: [
      { label: 'Facebook page link', noneLabel: "I don't have one" },
      { label: 'Instagram page link', noneLabel: "I don't have one" }
    ]
  },

  {
    id: 'goals',
    section: S_GOALS,
    type: 'checkboxes',
    title: 'What are your main goals with Adler Local?',
    description: 'Select up to 2 options.',
    options: GOALS,
    max: 2
  },
  {
    id: 'competitors',
    section: S_GOALS,
    type: 'fields',
    title: 'Who are your 2–3 biggest local competitors?',
    description:
      'Business names are fine. Add their websites if you know them.',
    fields: [
      { label: 'Competitor 1' },
      { label: 'Competitor 2' },
      { label: 'Competitor 3' }
    ]
  },

  {
    id: 'contact',
    section: S_TOUCH,
    type: 'contact',
    title: 'How and when should we contact you with updates?',
    options: ['Call', 'Text', 'Email'],
    extraField: { label: 'Best number or email, if different from above' }
  }
];

const LAYOUTS = [
  {
    name: 'Layout 1',
    url: 'https://hvac.adlerlaunchpad.com/',
    img: layoutHvac
  },
  {
    name: 'Layout 2',
    url: 'https://plumber.adlerlaunchpad.com/',
    img: layoutPlumber
  },
  {
    name: 'Layout 3',
    url: 'https://electrician.adlerlaunchpad.com/',
    img: layoutElectrician
  }
];

const WEBSITE_CONTENT_OPTIONS = [
  'Keep my wording and improve it for Google',
  'Write all-new content for me'
];

const getGoogleLocationKey = (location, index) =>
  String(location.name || location.id || location.locationId || index);

const getGoogleLocationLabel = (location) =>
  location.title ||
  location.locationName ||
  location.name ||
  location.address ||
  `Business location`;

const defaultHours = () => ({
  days: cloneWeekHours(defaultWeekHours())
});

/** Turns a stored answer into readable text for the review screen. */
const formatAnswer = (question, val) => {
  if (val == null || val === '') return null;
  switch (question.type) {
    case 'text':
    case 'phone':
    case 'email':
    case 'textarea':
      return String(val).trim() || null;
    case 'website':
      return val.none ? "I don't have a website" : val.url?.trim() || null;
    case 'address': {
      const parts = [val.street, val.suite].filter(Boolean);
      return parts.length ? parts.join(', ') : null;
    }
    case 'file':
    case 'files': {
      const files = val;
      return files.length ? files.map((f) => f.name).join(', ') : null;
    }
    case 'radio': {
      if (typeof val === 'object') {
        const choice = val.choice ?? '';
        if (!choice) return null;
        return val.extra?.trim() ? `${choice} — ${val.extra}` : choice;
      }
      return String(val).trim() || null;
    }
    case 'businessType': {
      const choice = val.choice ?? '';
      if (!choice) return null;
      return val.other?.trim() ? `${choice} — ${val.other}` : choice;
    }
    case 'checkboxes':
    case 'holidays': {
      const list = val;
      return list.length ? list.join(', ') : null;
    }
    case 'hours': {
      const h = val;
      const lines = WEEK_DAYS.map((d) => {
        const day = h.days?.[d.key];
        if (!day) return null;
        if (!day.isOpen) return `${d.label}: Closed`;
        if (day.open24) return `${d.label}: Open 24 hours`;
        if (day.openTime && day.closeTime)
          return `${d.label}: ${day.openTime} – ${day.closeTime}`;
        return null;
      }).filter(Boolean);
      if (h.emergency) lines.push(`Emergency service: ${h.emergency}`);
      return lines.length ? lines.join('\n') : null;
    }
    case 'fields': {
      const data = val;
      if (data.unsure) return "I'm not sure";
      const lines = question.fields
        .map((f, i) => {
          if (data.none?.[i]) return `${f.label}: ${f.noneLabel ?? 'None'}`;
          const value = f.password
            ? data.vals?.[i]
              ? '••••••••'
              : ''
            : data.vals?.[i]?.trim();
          return value ? `${f.label}: ${value}` : null;
        })
        .filter(Boolean);
      return lines.length ? lines.join('\n') : null;
    }
    case 'contact': {
      const parts = [
        val.method,
        val.time && `Best time: ${val.time}`,
        val.extra && `Contact: ${val.extra}`
      ].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }
    default:
      return null;
  }
};

const choiceCls =
  'flex items-center gap-3 rounded-xl border p-4 cursor-pointer hover:bg-muted/50 transition-colors';

const Onboarding = () => {
  const history = useHistory();
  const { selectedClient } = useSelector(selectedClientSelector);
  const [step, setStep] = useState(0);
  const [finished, setFinished] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [editingFromReview, setEditingFromReview] = useState(false);
  const celebratedRef = useRef(false);
  const [values, setValues] = useState({});
  const [connectHint, setConnectHint] = useState(null);
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleProfileLoading, setGoogleProfileLoading] = useState(false);
  const [googleLocations, setGoogleLocations] = useState([]);
  const [selectedGoogleLocationKey, setSelectedGoogleLocationKey] =
    useState('');
  const [googleError, setGoogleError] = useState('');
  const googlePopupRef = useRef(null);
  const googlePopupWatcherRef = useRef(null);
  const googlePopupCloseTimeoutRef = useRef(null);
  const googlePopupOriginRef = useRef(null);
  // Real sign-in isn't wired yet; once it is, flip these so already-connected questions are skipped.
  const [connected, setConnected] = useState({
    google: false,
    facebook: false
  });
  const [web, setWeb] = useState({});

  useEffect(
    () => () => {
      if (googlePopupWatcherRef.current) {
        window.clearInterval(googlePopupWatcherRef.current);
      }
      if (googlePopupCloseTimeoutRef.current) {
        window.clearTimeout(googlePopupCloseTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!finished || celebratedRef.current) return;
    celebratedRef.current = true;
    const css = getComputedStyle(document.documentElement);
    const brand = (lightness) => {
      const [h, s] = css.getPropertyValue('--primary').trim().split(/\s+/);
      return `hsl(${h} ${s} ${lightness})`;
    };
    const colors = [
      brand('44%'),
      brand('58%'),
      brand('72%'),
      '#F2B705',
      '#EF6351',
      '#2FB8A6',
      '#7C6BF2',
      '#F2A6C4'
    ];
    confetti({
      particleCount: 60,
      spread: 55,
      startVelocity: 22,
      gravity: 0.9,
      ticks: 150,
      scalar: 0.8,
      zIndex: 60,
      origin: { x: 0.5, y: 0.32 },
      colors,
      disableForReducedMotion: true
    });
    const soft = window.setTimeout(() => {
      confetti({
        particleCount: 28,
        spread: 40,
        startVelocity: 18,
        ticks: 130,
        scalar: 0.7,
        zIndex: 60,
        origin: { x: 0.5, y: 0.28 },
        colors,
        disableForReducedMotion: true
      });
    }, 260);
    return () => window.clearTimeout(soft);
  }, [finished]);

  const visible = useMemo(
    () => QUESTIONS.filter((q) => !q.show || q.show(values, connected)),
    [values, connected]
  );
  const total = visible.length;
  const isWebsite = step >= total;
  const q = visible[Math.min(step, total - 1)];
  const progress =
    finished || isWebsite ? 100 : Math.round((step / total) * 100);
  const counterLabel = isWebsite
    ? 'Final step'
    : `Question ${step + 1} of ${total}`;
  const v = values[q.id];
  const set = (val) => setValues((p) => ({ ...p, [q.id]: val }));
  const hasWebsite = !noWebsite(values);
  const canContinueFirstStep =
    !googleProfileLoading &&
    canContinueAfterGoogleConnection(
      googleConnected,
      googleLocations,
      selectedGoogleLocationKey
    );

  const applyGoogleLocation = (location) => {
    setValues((currentValues) => ({
      ...currentValues,
      ...mapGoogleLocationToAnswers(location)
    }));
  };

  useEffect(() => {
    const handleGoogleOAuthResult = async (event) => {
      if (
        event.source !== googlePopupRef.current ||
        (event.origin !== window.location.origin &&
          event.origin !== googlePopupOriginRef.current)
      ) {
        return;
      }

      const message = event.data;
      const isLegacyGoogleSuccess =
        message?.type === 'GOOGLE_OAUTH_SUCCESS' &&
        message.networkName === 'Google Business';
      if (
        !isLegacyGoogleSuccess &&
        message?.type !== 'GOOGLE_ONBOARDING_OAUTH_RESULT'
      ) {
        return;
      }

      if (googlePopupWatcherRef.current) {
        window.clearInterval(googlePopupWatcherRef.current);
        googlePopupWatcherRef.current = null;
      }
      if (googlePopupCloseTimeoutRef.current) {
        window.clearTimeout(googlePopupCloseTimeoutRef.current);
        googlePopupCloseTimeoutRef.current = null;
      }
      googlePopupRef.current = null;
      googlePopupOriginRef.current = null;
      setGoogleConnecting(false);

      if (isLegacyGoogleSuccess) {
        const location = message.location;
        setGoogleConnected(true);
        setConnected((current) => ({ ...current, google: true }));
        if (location) {
          setGoogleLocations([location]);
          setSelectedGoogleLocationKey(getGoogleLocationKey(location, 0));
          applyGoogleLocation(location);
        }
        setGoogleError('');
        setGoogleProfileLoading(true);
        try {
          const profileInfo = await getGoogleProfileInfo(
            message.clientId || selectedClient?.id
          );
          const profile = profileInfo.data?.data || profileInfo.data;
          const profileAnswers = mapGoogleLocationToAnswers(profile);
          if (Object.keys(profileAnswers).length > 0) {
            const profileLocation = {
              ...profile,
              ...location
            };
            setGoogleLocations([profileLocation]);
            setSelectedGoogleLocationKey(
              getGoogleLocationKey(profileLocation, 0)
            );
            applyGoogleLocation(profileLocation);
          } else if (!location) {
            setGoogleError(
              'Google connected, but profile details could not be loaded. You can continue and fill in the details manually.'
            );
          }
        } catch (error) {
          console.error('Error loading Google profile for onboarding:', error);
          if (!location) {
            setGoogleError(
              'Google connected, but profile details could not be loaded. You can continue and fill in the details manually.'
            );
          }
        } finally {
          setGoogleProfileLoading(false);
        }
        return;
      }

      if (!message.success) {
        setGoogleError(
          message.error ||
            'Could not connect Google Business Profile. Please try again.'
        );
        return;
      }

      const locations = message.locations;
      if (!Array.isArray(locations)) {
        setGoogleError('Google connected but did not return a locations list.');
        return;
      }

      setGoogleLocations(locations);
      setGoogleConnected(true);
      setConnected((current) => ({ ...current, google: true }));
      setSelectedGoogleLocationKey('');
      if (locations.length === 1) {
        setSelectedGoogleLocationKey(getGoogleLocationKey(locations[0], 0));
        applyGoogleLocation(locations[0]);
      }
      setGoogleError('');
    };

    window.addEventListener('message', handleGoogleOAuthResult);
    return () => window.removeEventListener('message', handleGoogleOAuthResult);
  }, [selectedClient?.id]);

  const handleGoogleConnect = () => {
    const clientId = selectedClient?.id;
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_GOOGLE_OAUTH_REDIRECT_URI;
    if (!clientId) {
      setGoogleError(
        'Select a client before connecting Google Business Profile.'
      );
      return;
    }
    if (!googleClientId || !redirectUri) {
      setGoogleError(
        'Google Business connection is not configured. Contact support.'
      );
      return;
    }

    try {
      googlePopupOriginRef.current = new URL(redirectUri).origin;
    } catch (error) {
      console.error('Invalid Google OAuth redirect URL:', error);
      setGoogleError(
        'Google Business connection has an invalid redirect URL. Contact support.'
      );
      return;
    }

    const state = encodeURIComponent(
      JSON.stringify({
        clientId,
        platform: 'adlersuite',
        openerOrigin: window.location.origin,
        onboarding: true
      })
    );
    const popup = initiateGoogleOAuth(googleClientId, redirectUri, state);
    if (!popup) {
      googlePopupOriginRef.current = null;
      setGoogleError(
        'Unable to open Google OAuth. Please allow popups and try again.'
      );
      return;
    }

    if (googlePopupCloseTimeoutRef.current) {
      window.clearTimeout(googlePopupCloseTimeoutRef.current);
      googlePopupCloseTimeoutRef.current = null;
    }
    setGoogleError('');
    setGoogleConnecting(true);
    googlePopupRef.current = popup;
    if (googlePopupWatcherRef.current) {
      window.clearInterval(googlePopupWatcherRef.current);
    }
    googlePopupWatcherRef.current = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(googlePopupWatcherRef.current);
        googlePopupWatcherRef.current = null;
        googlePopupCloseTimeoutRef.current = window.setTimeout(() => {
          if (googlePopupRef.current !== popup) return;
          googlePopupRef.current = null;
          googlePopupOriginRef.current = null;
          googlePopupCloseTimeoutRef.current = null;
          setGoogleConnecting(false);
          setGoogleError(
            (currentError) =>
              currentError ||
              'Google connection was cancelled or could not be completed.'
          );
        }, 1500);
      }
    }, 500);
  };

  const handleGoogleLocationChange = (locationKey) => {
    setSelectedGoogleLocationKey(locationKey);
    const selectedLocation = googleLocations.find(
      (location, index) => getGoogleLocationKey(location, index) === locationKey
    );
    if (selectedLocation) applyGoogleLocation(selectedLocation);
  };

  const reviewSections = useMemo(() => {
    const map = new Map();
    visible.forEach((qq, idx) => {
      if (!map.has(qq.section)) map.set(qq.section, []);
      map.get(qq.section).push({
        title: qq.title,
        answer: formatAnswer(qq, values[qq.id]),
        editStep: idx
      });
    });
    const webItems = [
      hasWebsite && { title: 'Website content', answer: web.content ?? null },
      { title: 'Website colors', answer: web.colors?.trim() || null },
      { title: 'Website layout', answer: web.layout ?? null }
    ]
      .filter(Boolean)
      .map((i) => ({ ...i, editStep: visible.length }));
    if (webItems.some((i) => i.answer)) map.set('Website', webItems);
    return [...map.entries()];
  }, [visible, values, web, hasWebsite]);

  const renderInput = () => {
    switch (q.type) {
      case 'text':
      case 'phone':
      case 'email':
        return (
          <Input
            type={q.type === 'phone' ? 'tel' : q.type}
            value={v ?? ''}
            onChange={(e) => set(e.target.value)}
            placeholder={q.placeholder}
            className="h-14 text-base"
          />
        );
      case 'website':
        return (
          <div className="space-y-3">
            <Input
              type="text"
              inputMode="url"
              disabled={!!v?.none}
              value={v?.url ?? ''}
              onChange={(e) => set({ ...v, url: e.target.value })}
              placeholder={q.placeholder}
              className="h-14 text-base"
            />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={!!v?.none}
                onCheckedChange={(c) => set({ ...v, none: !!c })}
              />
              I don't have a website
            </label>
          </div>
        );
      case 'address':
        return (
          <div className="space-y-3">
            <Input
              value={v?.street ?? ''}
              onChange={(e) => set({ ...v, street: e.target.value })}
              placeholder="123 Main St, Miami, FL 33101"
              className="h-14 text-base"
            />
            <Input
              value={v?.suite ?? ''}
              onChange={(e) => set({ ...v, suite: e.target.value })}
              placeholder="Suite / Unit (optional)"
              className="h-14 text-base"
            />
          </div>
        );
      case 'textarea':
        return (
          <Textarea
            value={v ?? ''}
            onChange={(e) => set(e.target.value)}
            placeholder={q.placeholder}
            className="min-h-[160px] text-base"
          />
        );
      case 'file':
      case 'files': {
        const files = v ?? [];
        return (
          <div className="space-y-3">
            <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-8 cursor-pointer hover:bg-muted/50 transition-colors">
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">
                Click to upload {q.type === 'files' ? 'photos' : 'your logo'}
              </span>
              <span className="text-xs text-muted-foreground">
                {q.type === 'file'
                  ? 'PNG, JPG or SVG, under 2MB.'
                  : 'Multiple files allowed.'}
              </span>
              <input
                type="file"
                className="hidden"
                multiple={q.type === 'files'}
                accept={
                  q.type === 'file' ? '.png,.jpg,.jpeg,.svg' : '.png,.jpg,.jpeg'
                }
                onChange={(e) => {
                  const list = Array.from(e.target.files ?? []);
                  set(
                    q.type === 'files' ? [...files, ...list] : list.slice(0, 1)
                  );
                  e.target.value = '';
                }}
              />
            </label>
            {files.map((f, i) => (
              <div
                key={i}
                className="flex items-center justify-between rounded-xl border px-3 py-2 text-sm">
                <span className="truncate">{f.name}</span>
                <button
                  onClick={() => set(files.filter((_, j) => j !== i))}
                  aria-label="Remove file">
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            ))}
          </div>
        );
      }
      case 'radio': {
        const choice = typeof v === 'object' ? v?.choice ?? '' : v ?? '';
        const extra = typeof v === 'object' ? v?.extra ?? '' : '';
        const wrap = !!q.followUp;
        return (
          <div className="space-y-4">
            <RadioGroup
              value={choice}
              onValueChange={(o) => set(wrap ? { choice: o, extra } : o)}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {q.options.map((o) => (
                <label key={o} className={choiceCls}>
                  <RadioGroupItem value={o} />
                  <span className="text-sm">{o}</span>
                </label>
              ))}
            </RadioGroup>
            {q.followUp && choice && q.followUp.when(choice) && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {q.followUp.label}
                </label>
                <Input
                  value={extra}
                  onChange={(e) => set({ choice, extra: e.target.value })}
                  className="h-14 text-base"
                />
              </div>
            )}
            {q.note && choice && q.note.when(choice) && (
              <p className="flex items-start gap-2 rounded-xl bg-primary/5 p-3 text-sm text-muted-foreground">
                <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                {q.note.text}
              </p>
            )}
          </div>
        );
      }
      case 'businessType': {
        const type = v?.type ?? '';
        const opts = type === HOME_TYPE ? HOME_SERVICES : OTHER_INDUSTRIES;
        return (
          <div className="space-y-5">
            <RadioGroup
              value={type}
              onValueChange={(t) => set({ type: t, choice: '' })}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {TYPE_OPTIONS.map((o) => (
                <label key={o} className={choiceCls}>
                  <RadioGroupItem value={o} />
                  <span className="text-sm">{o}</span>
                </label>
              ))}
            </RadioGroup>
            {type && (
              <div className="space-y-2">
                <Select
                  value={v?.choice ?? ''}
                  onValueChange={(c) => set({ ...v, choice: c })}>
                  <SelectTrigger className="h-14 text-base">
                    <SelectValue placeholder="Select your business category" />
                  </SelectTrigger>
                  <SelectContent>
                    {opts.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Pick the one that matches most of your work. This becomes your
                  main category on Google.
                </p>
                {v?.choice?.startsWith('Other') && (
                  <Input
                    value={v?.other ?? ''}
                    onChange={(e) => set({ ...v, other: e.target.value })}
                    placeholder="Tell us what you do (e.g., Mobile auto detailing)"
                    className="h-14 text-base"
                  />
                )}
              </div>
            )}
          </div>
        );
      }
      case 'checkboxes': {
        const list = v ?? [];
        const toggle = (o) => {
          if (list.includes(o)) return set(list.filter((x) => x !== o));
          if (q.exclusive && o === q.exclusive) return set([o]);
          const next = [...list.filter((x) => x !== q.exclusive), o];
          if (q.max && next.length > q.max) return;
          set(next);
        };
        const full = !!q.max && list.length >= q.max;
        return (
          <div
            className={`grid grid-cols-1 gap-3 ${
              q.max ? '' : 'sm:grid-cols-2'
            }`}>
            {q.options.map((o) => {
              const disabled = full && !list.includes(o);
              return (
                <label
                  key={o}
                  className={`${choiceCls} p-3 ${
                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}>
                  <Checkbox
                    checked={list.includes(o)}
                    disabled={disabled}
                    onCheckedChange={() => toggle(o)}
                  />
                  <span className="text-sm">{o}</span>
                </label>
              );
            })}
          </div>
        );
      }
      case 'holidays': {
        const list = v ?? [];
        const none = list.includes('None');
        const toggle = (o) =>
          set(
            list.includes(o)
              ? list.filter((x) => x !== o)
              : [...list.filter((x) => x !== 'None'), o]
          );
        const group = (label, items) => (
          <div className="space-y-2">
            <p className="text-sm font-medium">{label}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {items.map((o) => (
                <label key={o} className={`${choiceCls} p-3`}>
                  <Checkbox
                    checked={list.includes(o)}
                    onCheckedChange={() => toggle(o)}
                  />
                  <span className="text-sm">{o}</span>
                </label>
              ))}
            </div>
          </div>
        );
        return (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() => set([...ALL_HOLIDAYS])}>
                Select all
              </Button>
              <label className="flex items-center gap-2 rounded-full border px-4 text-sm cursor-pointer">
                <Checkbox
                  checked={none}
                  onCheckedChange={(c) => set(c ? ['None'] : [])}
                />
                None
              </label>
            </div>
            {group('United States', US_HOLIDAYS)}
            {group('Canada', CA_HOLIDAYS)}
          </div>
        );
      }
      case 'hours': {
        const hours = v ?? defaultHours();
        return (
          <div className="space-y-6">
            <BusinessHoursSelect
              value={hours.days}
              onChange={(days) => set({ ...hours, days })}
              allow24
            />
            <div className="space-y-3">
              <p className="text-sm font-medium">
                Do you offer emergency service?
              </p>
              <RadioGroup
                value={hours.emergency ?? ''}
                onValueChange={(o) => set({ ...hours, emergency: o })}
                className="grid grid-cols-2 gap-3">
                {['Yes', 'No'].map((o) => (
                  <label key={o} className={choiceCls}>
                    <RadioGroupItem value={o} />
                    <span className="text-sm">{o}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>
        );
      }
      case 'fields': {
        const data = v ?? {};
        const vals = data.vals ?? [];
        const none = data.none ?? [];
        return (
          <div className="space-y-4">
            {q.fields.map((f, i) => (
              <div key={f.label} className="space-y-2">
                <label className="text-sm font-medium">{f.label}</label>
                <Input
                  type={f.password ? 'password' : 'text'}
                  autoComplete={f.password ? 'new-password' : 'off'}
                  disabled={!!data.unsure || !!none[i]}
                  value={vals[i] ?? ''}
                  placeholder={f.placeholder}
                  onChange={(e) => {
                    const n = [...vals];
                    n[i] = e.target.value;
                    set({ ...data, vals: n });
                  }}
                  className="h-14 text-base"
                />
                {f.noneLabel && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={!!none[i]}
                      onCheckedChange={(c) => {
                        const n = [...none];
                        n[i] = !!c;
                        set({ ...data, none: n });
                      }}
                    />
                    {f.noneLabel}
                  </label>
                )}
              </div>
            ))}
            {q.unsureLabel && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={!!data.unsure}
                  onCheckedChange={(c) => set({ ...data, unsure: !!c })}
                />
                {q.unsureLabel}
              </label>
            )}
          </div>
        );
      }
      case 'contact':
        return (
          <div className="space-y-5">
            <RadioGroup
              value={v?.method ?? ''}
              onValueChange={(m) => set({ ...v, method: m })}
              className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {q.options.map((o) => (
                <label key={o} className={choiceCls}>
                  <RadioGroupItem value={o} />
                  <span className="text-sm">{o}</span>
                </label>
              ))}
            </RadioGroup>
            <div className="space-y-2">
              <label className="text-sm font-medium">Best time of day</label>
              <Input
                value={v?.time ?? ''}
                onChange={(e) => set({ ...v, time: e.target.value })}
                placeholder="e.g. Weekday mornings, 9–11am"
                className="h-14 text-base"
              />
            </div>
            {q.extraField && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {q.extraField.label}
                </label>
                <Input
                  value={v?.extra ?? ''}
                  onChange={(e) => set({ ...v, extra: e.target.value })}
                  className="h-14 text-base"
                />
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div className="min-h-screen bg-muted/40 flex flex-col">
      <header className="bg-background border-b">
        <div className="mx-auto w-full max-w-2xl px-4 py-4 flex items-center justify-between gap-3">
          <img src={logo} alt="Adler Local" className="h-8" />
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={finished || step === 0}
              aria-label="Go to previous question"
              title="Previous question">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full"
              onClick={() => setStep((s) => Math.min(total, s + 1))}
              disabled={
                finished || isWebsite || (step === 0 && !canContinueFirstStep)
              }
              aria-label="Go to next question"
              title="Next question">
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <Progress value={progress} className="h-1 rounded-none" />
      </header>

      <main className="flex-1 flex items-start justify-center px-4 pt-3 pb-10 md:pt-4 md:pb-16">
        <div className="w-full max-w-2xl space-y-4">
          {!finished && (
            <div className="flex justify-end">
              <span className="rounded-full border bg-background px-3 py-1 text-xs font-medium tabular-nums text-muted-foreground shadow-sm">
                {counterLabel}
              </span>
            </div>
          )}
          {!finished && step === 0 && (
            <div className="bg-background rounded-3xl border shadow-sm p-5 md:p-6">
              <h2 className="text-lg md:text-xl font-bold mb-3">
                Welcome to Adler Local! Let's get you set up.
              </h2>
              <p className="text-sm text-muted-foreground mb-3">
                To get your business found on Google, we need a few details.
                Start by connecting your Google Business Profile — your listing
                on Google Search and Maps. We need it to start your service.
                Once it's connected, we'll fill in most of this form for you.
              </p>
              <p className="text-sm text-muted-foreground">
                Then connect your business Facebook page so we can post for you.
              </p>
              <div className="grid grid-cols-1 gap-3 mt-5">
                <Button
                  type="button"
                  className="h-14 w-full rounded-full"
                  onClick={handleGoogleConnect}
                  disabled={
                    googleConnecting || googleConnected || !selectedClient?.id
                  }>
                  {googleConnecting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <LogIn className="h-4 w-4 mr-2 shrink-0" />
                  )}
                  <span className="truncate">
                    {googleConnecting
                      ? 'Connecting Google Business Profile...'
                      : googleConnected
                      ? 'Google Business Profile connected'
                      : 'Connect Google Business Profile'}
                  </span>
                </Button>
                {!selectedClient?.id && (
                  <p className="text-sm text-destructive">
                    Select a client in Adler Suite before connecting Google
                    Business Profile.
                  </p>
                )}
                <Button
                  className="h-14 w-full rounded-full"
                  onClick={() => setConnectHint('facebook')}>
                  <LogIn className="h-4 w-4 mr-2 shrink-0" />
                  <span className="truncate">Connect Facebook</span>
                </Button>
              </div>

              {googleConnected && googleLocations.length > 1 && (
                <div className="space-y-2 mt-4">
                  <label
                    htmlFor="google-business-location"
                    className="text-sm font-medium">
                    Choose your Google Business location
                  </label>
                  <Select
                    value={selectedGoogleLocationKey}
                    onValueChange={handleGoogleLocationChange}>
                    <SelectTrigger
                      id="google-business-location"
                      className="h-12">
                      <SelectValue placeholder="Select a business location" />
                    </SelectTrigger>
                    <SelectContent>
                      {googleLocations.map((location, index) => {
                        const locationKey = getGoogleLocationKey(
                          location,
                          index
                        );
                        return (
                          <SelectItem key={locationKey} value={locationKey}>
                            {getGoogleLocationLabel(location)}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {googleError && (
                <p
                  role="alert"
                  className="flex items-start gap-2 text-sm text-destructive mt-4">
                  <Info className="h-4 w-4 mt-0.5 shrink-0" />
                  {googleError}
                </p>
              )}

              {googleConnected && (
                <p
                  role="status"
                  className="flex items-start gap-2 text-sm text-muted-foreground mt-4">
                  <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  {googleProfileLoading
                    ? 'Google connected. Loading your Business Profile details...'
                    : googleLocations.length === 0
                    ? 'Google connected. No Business Profile locations were returned; you can continue and fill in the details manually.'
                    : googleLocations.length > 1 && !selectedGoogleLocationKey
                    ? 'Google connected. Choose a location to prefill the onboarding form.'
                    : 'Google connected. We filled in the available business details; review and edit them as you continue.'}
                </p>
              )}

              {connectHint && (
                <p className="flex items-start gap-2 text-sm text-muted-foreground mt-4 pt-4 border-t">
                  <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  Facebook sign-in isn't available yet. Answer the questions
                  below for now.
                </p>
              )}
              <p className="text-sm text-muted-foreground mt-4 pt-4 border-t">
                <span className="font-semibold text-foreground">Stuck?</span>{' '}
                Call us at{' '}
                <a
                  href="tel:8888846050"
                  className="text-primary font-medium hover:underline">
                  888-884-6050
                </a>{' '}
                and we'll walk you through it.
              </p>
            </div>
          )}
          {finished ? (
            <div className="bg-background rounded-3xl border p-8 md:p-12 shadow-sm text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <Check className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mb-3">
                You're all set!
              </h1>
              <p className="text-muted-foreground mb-6">
                Thanks — your answers are in. We'll use them to shape your
                strategy.
              </p>
              <div className="flex justify-center mb-8">
                <Button
                  variant="outline"
                  className="h-14 w-full sm:w-auto sm:px-10 rounded-full"
                  onClick={() => setShowReview((s) => !s)}>
                  <ClipboardList className="h-4 w-4 mr-2" />
                  {showReview ? 'Hide my answers' : 'Review my answers'}
                  {showReview ? (
                    <ChevronUp className="h-4 w-4 ml-2" />
                  ) : (
                    <ChevronDown className="h-4 w-4 ml-2" />
                  )}
                </Button>
              </div>
              {showReview && (
                <div className="text-left rounded-2xl border bg-muted/40 p-5 mb-8 max-h-[420px] overflow-y-auto space-y-6">
                  {reviewSections.map(([section, items]) => (
                    <div key={section}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">
                        {section}
                      </p>
                      <div className="space-y-2">
                        {items.map((item) => (
                          <div
                            key={item.title}
                            className="flex items-start justify-between gap-3 text-sm">
                            <div className="min-w-0">
                              <p className="text-muted-foreground">
                                {item.title}
                              </p>
                              <p className="font-medium whitespace-pre-line">
                                {item.answer ?? (
                                  <span className="italic text-muted-foreground">
                                    No answer
                                  </span>
                                )}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0 rounded-full"
                              aria-label={`Edit: ${item.title}`}
                              title="Edit this answer"
                              onClick={() => {
                                setShowReview(false);
                                setEditingFromReview(true);
                                setFinished(false);
                                setStep(item.editStep);
                              }}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Button
                className="h-14 w-full sm:w-auto sm:px-10 rounded-full"
                onClick={() => history.push('/client')}>
                Go to Dashboard
              </Button>
            </div>
          ) : isWebsite ? (
            <div className="bg-background rounded-3xl border p-6 md:p-10 shadow-sm space-y-10">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                Website
              </p>

              {hasWebsite && (
                <div className="space-y-3">
                  <h2 className="text-xl md:text-2xl font-bold">
                    Website content
                  </h2>
                  <p className="text-muted-foreground">
                    Do you want to keep your current website's wording, or
                    should we write new content?
                  </p>
                  <RadioGroup
                    value={web.content ?? ''}
                    onValueChange={(o) => setWeb((w) => ({ ...w, content: o }))}
                    className="grid grid-cols-1 gap-3">
                    {WEBSITE_CONTENT_OPTIONS.map((o) => (
                      <label key={o} className={choiceCls}>
                        <RadioGroupItem value={o} />
                        <span className="text-sm">{o}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              )}

              <div className="space-y-3">
                <h2 className="text-xl md:text-2xl font-bold">Colors</h2>
                <p className="text-muted-foreground">
                  What colors do you want for your website? List your brand
                  colors, or leave blank and we'll match your logo.
                </p>
                <Input
                  value={web.colors ?? ''}
                  onChange={(e) =>
                    setWeb((w) => ({ ...w, colors: e.target.value }))
                  }
                  placeholder="e.g. Navy blue and orange"
                  className="h-14 text-base"
                />
              </div>

              <div className="space-y-3">
                <h2 className="text-xl md:text-2xl font-bold">
                  Website layout
                </h2>
                <p className="text-muted-foreground">
                  Which layout would you like for your website? These layouts
                  are proven to turn the most website visitors into calls and
                  new customers.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {LAYOUTS.map((l) => {
                    const selected = web.layout === l.name;
                    return (
                      <div
                        key={l.name}
                        className={`group rounded-2xl border p-2 cursor-pointer transition-all hover:shadow-md ${
                          selected ? 'border-primary ring-2 ring-primary' : ''
                        }`}
                        onClick={() =>
                          setWeb((w) => ({ ...w, layout: l.name }))
                        }
                        role="button"
                        aria-pressed={selected}>
                        <div className="relative overflow-hidden rounded-xl">
                          <img
                            src={l.img}
                            alt={`${l.name} website layout`}
                            className="w-full object-cover object-top aspect-[4/3]"
                          />
                          {selected && (
                            <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary shadow-md">
                              <Check className="h-3.5 w-3.5 text-primary-foreground" />
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2 px-2 pt-3 pb-1">
                          <span className="whitespace-nowrap text-sm font-semibold">
                            {l.name}
                          </span>
                          <a
                            href={l.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex h-6 shrink-0 items-center gap-1 rounded-full border border-primary/30 px-2 text-[11px] font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground">
                            Preview
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  variant="outline"
                  className="h-14 w-full sm:w-auto px-8 has-[>svg]:px-8 rounded-full"
                  onClick={() => {
                    if (editingFromReview) {
                      setEditingFromReview(false);
                      setShowReview(true);
                      setFinished(true);
                    } else {
                      setStep(total - 1);
                    }
                  }}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {editingFromReview ? 'Back to review' : 'Back'}
                </Button>
                <Button
                  className="h-14 w-full sm:flex-1 rounded-full"
                  onClick={() => setFinished(true)}>
                  Submit
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-background rounded-3xl border p-6 md:p-10 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-3">
                {q.section}
              </p>
              <h1 className="text-xl md:text-2xl font-bold mb-2">{q.title}</h1>
              {q.description ? (
                <p className="text-muted-foreground mb-6">{q.description}</p>
              ) : (
                <div className="mb-6" />
              )}

              {renderInput()}

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                {(step > 0 || editingFromReview) && (
                  <Button
                    variant="outline"
                    className="h-14 w-full sm:w-auto px-8 has-[>svg]:px-8 rounded-full"
                    onClick={() => {
                      if (editingFromReview) {
                        setEditingFromReview(false);
                        setShowReview(true);
                        setFinished(true);
                      } else {
                        setStep((s) => s - 1);
                      }
                    }}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {editingFromReview ? 'Back to review' : 'Back'}
                  </Button>
                )}
                <Button
                  className="h-14 w-full sm:flex-1 rounded-full"
                  disabled={step === 0 && !canContinueFirstStep}
                  onClick={() => {
                    if (editingFromReview) {
                      setEditingFromReview(false);
                      setShowReview(true);
                      setFinished(true);
                    } else {
                      setStep((s) => s + 1);
                    }
                  }}>
                  {editingFromReview ? 'Save and review' : 'Continue'}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="py-6 text-center">
        <p className="text-xs text-muted-foreground">© 2026 Adler Local</p>
      </footer>
    </div>
  );
};

export default Onboarding;
